import { Infinity as InfinityIcon, CircleSlash } from 'lucide-react';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { AssetType } from '../../types/emissions';
import { getAssetTypeBrand } from '../../config/assetTypeBranding';

const ASSET_TYPE_OPTIONS = Object.values(AssetType);

export type AssetFilterValue = AssetType | 'all' | 'unassigned';

interface AssetTypeFilterProps {
  value: AssetFilterValue;
  onChange: (value: AssetFilterValue) => void;
  /** Asset types to show as disabled (e.g. types with no assets) */
  disabledTypes?: Set<AssetType>;
}

export const AssetTypeFilter = ({ value, onChange, disabledTypes }: AssetTypeFilterProps) => {
  const columnCount = ASSET_TYPE_OPTIONS.length + 2; // +1 for All, +1 for Unassigned

  return (
    <div className='tw:space-y-2'>
      <span className='tw:text-sm tw:font-medium tw:text-muted-foreground'>Asset Type</span>
      <div className='tw:grid tw:gap-2' style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}>
        <Card
          onClick={() => onChange('all')}
          className={cn(
            'tw:cursor-pointer tw:transition-all tw:border-2',
            value === 'all'
              ? 'tw:border-blue-500 tw:bg-blue-50/50'
              : 'tw:border-transparent tw:hover:border-muted-foreground/20',
          )}
        >
          <CardContent className='tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:py-3 tw:px-2'>
            <div className='tw:rounded-full tw:p-2 tw:bg-muted'>
              <InfinityIcon className='tw:size-4 tw:text-muted-foreground' />
            </div>
            <span className={cn('tw:text-xs tw:font-medium', value === 'all' ? 'tw:text-blue-600' : '')}>All</span>
          </CardContent>
        </Card>
        {ASSET_TYPE_OPTIONS.map((type) => {
          const brand = getAssetTypeBrand(type);
          const isActive = value === type;
          const isDisabled = disabledTypes?.has(type) ?? false;
          return (
            <Card
              key={type}
              onClick={() => !isDisabled && onChange(type)}
              className={cn(
                'tw:transition-all tw:border-2',
                isDisabled
                  ? 'tw:opacity-40 tw:cursor-not-allowed'
                  : 'tw:cursor-pointer',
                isActive && !isDisabled
                  ? 'tw:border-blue-500 tw:bg-blue-50/50'
                  : 'tw:border-transparent',
                !isActive && !isDisabled && 'tw:hover:border-muted-foreground/20',
              )}
            >
              <CardContent className='tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:py-3 tw:px-2'>
                <div className={cn('tw:rounded-full tw:p-2', brand.iconBg)}>
                  <brand.Icon className={cn('tw:size-4', brand.iconColor)} />
                </div>
                <div className={cn('tw:text-xs tw:font-medium tw:text-center', isActive && !isDisabled ? 'tw:text-blue-600' : '')}>{brand.label}</div>
              </CardContent>
            </Card>
          );
        })}
        <Card
            onClick={() => onChange('unassigned')}
            className={cn(
              'tw:cursor-pointer tw:transition-all tw:border-2',
              value === 'unassigned'
                ? 'tw:border-blue-500 tw:bg-blue-50/50'
                : 'tw:border-transparent tw:hover:border-muted-foreground/20',
            )}
          >
            <CardContent className='tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:py-3 tw:px-2'>
              <div className='tw:rounded-full tw:p-2 tw:bg-gray-100'>
                <CircleSlash className='tw:size-4 tw:text-gray-500' />
              </div>
              <span className={cn('tw:text-xs tw:font-medium', value === 'unassigned' ? 'tw:text-blue-600' : '')}>Unassigned</span>
            </CardContent>
          </Card>
      </div>
    </div>
  );
};
